import React, { useEffect, useState } from "react";
import {
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth } from "../firebase";
import { getMe, updateProfile } from "../services/api";
import Sidebar from "./Sidebar";
import "./SettingsPage.css";

function Field({ label, hint, children }) {
  return (
    <div className="sf-field">
      <label className="sf-label">{label}</label>
      {children}
      {hint && <span className="sf-hint">{hint}</span>}
    </div>
  );
}

function StatusMsg({ msg }) {
  if (!msg) return null;
  const isOk = msg.startsWith("✓");
  return <p className={`sf-msg ${isOk ? "sf-msg--ok" : "sf-msg--err"}`}>{msg}</p>;
}

async function reauth(password) {
  const user = auth.currentUser;
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
}

export default function SettingsPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // username
  const [username, setUsername] = useState("");
  const [usernameMsg, setUsernameMsg] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);

  // password
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // email
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await getMe();
        setMe(data);
        setUsername(data.username || "");
      } catch {}
      setLoading(false);
    }
    load();
  }, []);

  async function handleUsername(e) {
    e.preventDefault();
    const u = username.trim();
    if (!u) return;
    if (u.length < 2 || u.length > 30) {
      setUsernameMsg("Username must be 2–30 characters");
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(u)) {
      setUsernameMsg("Only letters, numbers, and underscores allowed");
      return;
    }
    setSavingUsername(true);
    setUsernameMsg("");
    try {
      await updateProfile({ username: u });
      setUsernameMsg("✓ Username updated");
      setMe((prev) => ({ ...prev, username: u }));
    } catch (err) {
      setUsernameMsg(err.message || "Failed to update username");
    } finally {
      setSavingUsername(false);
    }
  }

  async function handlePassword(e) {
    e.preventDefault();
    if (!curPwd || !newPwd) return;
    if (newPwd.length < 6) { setPwdMsg("New password must be at least 6 characters"); return; }
    setSavingPwd(true);
    setPwdMsg("");
    try {
      await reauth(curPwd);
      await updatePassword(auth.currentUser, newPwd);
      setPwdMsg("✓ Password updated");
      setCurPwd(""); setNewPwd("");
    } catch (err) {
      setPwdMsg(err.message || "Failed to update password");
    } finally {
      setSavingPwd(false);
    }
  }

  async function handleEmail(e) {
    e.preventDefault();
    if (!newEmail || !emailPwd) return;
    setSavingEmail(true);
    setEmailMsg("");
    try {
      await reauth(emailPwd);
      await updateEmail(auth.currentUser, newEmail);
      setEmailMsg("✓ Email updated — please sign in again.");
      setNewEmail(""); setEmailPwd("");
    } catch (err) {
      setEmailMsg(err.message || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  }

  const displayName = me?.username || me?.email?.split("@")[0] || "Trader";

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content-area">
        <div className="page-header">
          <div>
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Manage your profile, username, and account details.</p>
          </div>
        </div>

        {loading ? (
          <p style={{ color: "#6a7e9c" }}>Loading…</p>
        ) : (
          <div className="sf-grid">

            {/* Profile card */}
            <div className="sf-card sf-card--profile">
              <div className="sf-avatar-lg">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="sf-profile-name">
                  {displayName}
                  {me?.plan === "pro" && <span className="sf-pro-badge">PRO</span>}
                </div>
                <div className="sf-profile-email">{me?.email}</div>
                <div className="sf-profile-plan">
                  {me?.plan === "pro" ? "Pro Member" : "Starter Plan"}
                </div>
              </div>
            </div>

            {/* Username */}
            <div className="sf-card">
              <div className="sf-card-title">Username</div>
              <p className="sf-card-desc">
                Shown in groups, comments, and your profile across Tradernal.
              </p>
              <form onSubmit={handleUsername} className="sf-form">
                <Field label="Username" hint="Letters, numbers, and underscores only">
                  <input
                    className="sf-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. tradermartin"
                    maxLength={30}
                  />
                </Field>
                <button className="sf-btn" type="submit" disabled={savingUsername}>
                  {savingUsername ? "Saving…" : "Save Username"}
                </button>
                <StatusMsg msg={usernameMsg} />
              </form>
            </div>

            {/* Password */}
            <div className="sf-card">
              <div className="sf-card-title">Change Password</div>
              <p className="sf-card-desc">
                Enter your current password, then choose a new one.
              </p>
              <form onSubmit={handlePassword} className="sf-form">
                <Field label="Current Password">
                  <input
                    className="sf-input"
                    type="password"
                    value={curPwd}
                    onChange={(e) => setCurPwd(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </Field>
                <Field label="New Password">
                  <input
                    className="sf-input"
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    minLength={6}
                  />
                </Field>
                <button className="sf-btn" type="submit" disabled={savingPwd}>
                  {savingPwd ? "Saving…" : "Update Password"}
                </button>
                <StatusMsg msg={pwdMsg} />
              </form>
            </div>

            {/* Email */}
            <div className="sf-card">
              <div className="sf-card-title">Change Email</div>
              <p className="sf-card-desc">
                Requires your current password. You'll be signed out after updating.
              </p>
              <form onSubmit={handleEmail} className="sf-form">
                <Field label="New Email">
                  <input
                    className="sf-input"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="new@email.com"
                  />
                </Field>
                <Field label="Current Password">
                  <input
                    className="sf-input"
                    type="password"
                    value={emailPwd}
                    onChange={(e) => setEmailPwd(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </Field>
                <button className="sf-btn" type="submit" disabled={savingEmail}>
                  {savingEmail ? "Saving…" : "Update Email"}
                </button>
                <StatusMsg msg={emailMsg} />
              </form>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}